import { useTheme } from '@mui/material/styles';
import { Fragment } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { ScatterChart, Scatter, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import ChartContainer from '../../shared/ChartContainer';


const colorMax = 'AC2E9D';
const colorRanges: { [key: number]: string } = {
    0: "EFF1F5",
    50: "D3D4D5",
    100: "4079D6",
    150: "5A67C8"
};

const chartLegends = {
    "151+": {
        min: `#${colorRanges[150]}`,
        max: `#${colorMax}`,
    },
    "101-151": {
        min: `#${colorRanges[100]}`,
        max: `#${colorRanges[150]}`,
    },
    "50-100": {
        min: `#${colorRanges[50]}`,
        max: `#${colorRanges[100]}`,
    },
    "0-50": {
        min: `#${colorRanges[0]}`,
        max: `#${colorRanges[50]}`,
    },
};


const selectColor = (val: number): [string, string] => {
    const sortedKeys: number[] = Object.keys(colorRanges).map(Number);
    let colorStart = colorRanges[0];
    let colorEnd = colorMax;
    sortedKeys.forEach((key) => {
        if (val > key) {
            colorStart = colorRanges[key];
            const nextKey = Number(key + 50);
            if (nextKey in colorRanges) {
                colorEnd = colorRanges[nextKey];
            } else {
                colorEnd = colorMax;
            }
        }
    });

    return [colorStart, colorEnd];
}

const hex = (x: number) => {
    const newX = x.toString(16);
    return (newX.length === 1) ? '0' + newX : newX;
};

const calculateColor = (val: number) => {
    const [colorStart, colorEnd] = selectColor(val);
    const ratio = (val > 200) || (val % 50) === 0 ? 1 : (val % 50) / 50;


    const r = Math.ceil(parseInt(colorEnd.substring(0, 2), 16) * ratio + parseInt(colorStart.substring(0, 2), 16) * (1 - ratio));
    const g = Math.ceil(parseInt(colorEnd.substring(2, 4), 16) * ratio + parseInt(colorStart.substring(2, 4), 16) * (1 - ratio));
    const b = Math.ceil(parseInt(colorEnd.substring(4, 6), 16) * ratio + parseInt(colorStart.substring(4, 6), 16) * (1 - ratio));

    return '#' + hex(r) + hex(g) + hex(b);
}

const Square = (props: any): JSX.Element => {
    const width = 32;
    const height = 32;
    return (
        <Fragment>
            <filter x="-50%" y="-50%" width="200%" height="200%" filterUnits="objectBoundingBox" id="shadow-filter">
                <feOffset dx="0" dy="1" in="SourceAlpha" result="shadowOffsetOuter1" />
                <feGaussianBlur stdDeviation="4" in="shadowOffsetOuter1" result="shadowBlurOuter1" />
                <feColorMatrix values="0 0 0 0 0   0 0 0 0 0   0 0 0 0 0  0 0 0 0.2 0" in="shadowBlurOuter1" type="matrix" result="shadowMatrixOuter1" />
                <feMerge>
                    <feMergeNode in="shadowMatrixOuter1" />
                    <feMergeNode in="SourceGraphic" />
                </feMerge>
            </filter>
            <rect
                id={`${props.key}-${Date.now()}`}
                fill={calculateColor(props.value)}
                x={props.cx}
                y={((width + 2) * (7 - props.index)) - 8}
                width={width}
                height={height}
            />
        </Fragment>

    );
}

const AxisTick = (props: any) => {

    const { x, y, payload } = props;

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={32}
                y={0}
                textAnchor="end"
                fontFamily="Montserrat"
                fontSize="0.9rem"
                fontWeight="bold"
            >
                {payload.value}
            </text>
        </g>
    );

};


const YAxisTick = (props: any) => {
    const { x, y, payload, visibleTicksCount } = props;
    const yPos = (payload.index / visibleTicksCount) * 32;

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={10}
                y={0 + yPos}
                textAnchor="end"
                fontFamily="Montserrat"
                fontSize="0.9rem"
                fontWeight="bold"
            >
                {payload.value}
            </text>
        </g>
    );

};


export interface TimeOfDayEventData {
    hour: string;
    value: number;
    day: string;
}


export interface EventsByTimeOfDayChartProps {
    data: TimeOfDayEventData[];
}

const hoverDiff = 6;

const handleOver = (data: any, index: any, e: any) => {
    const rect = e.target;
    const r = document.getElementById(rect.id);

    if (r) {
        const prevX = r.getAttribute('x');
        const prevY = r.getAttribute('y');
        r.setAttribute('height', rect.height.baseVal.value + hoverDiff);
        r.setAttribute('width', rect.width.baseVal.value + hoverDiff);
        if (prevX) {
            r.setAttribute('x', (rect.x.baseVal.value - (hoverDiff / 2)).toString());
        }
        if (prevY) {
            r.setAttribute('y', (rect.y.baseVal.value - (hoverDiff / 2)).toString());
        }
        r.setAttribute('filter', 'url(#shadow-filter)');
    }

}

const handleOut = (data: any, index: any, e: any) => {
    const rect = e.target;
    const r = document.getElementById(rect.id);

    if (r) {

        const prevHeight = r.getAttribute('height');
        const prevWidth = r.getAttribute('width');
        const prevX = r.getAttribute('x');
        const prevY = r.getAttribute('y');
        if (prevHeight) {
            r.setAttribute('height', (Number(prevHeight) - hoverDiff).toString());
        }
        if (prevWidth) {
            r.setAttribute('width', (Number(prevWidth) - hoverDiff).toString());
        }
        if (prevX) {
            r.setAttribute('x', (rect.x.baseVal.value + (hoverDiff / 2)).toString());
        }
        if (prevY) {
            r.setAttribute('y', (rect.y.baseVal.value + (hoverDiff / 2)).toString());
        }
        r.setAttribute('filter', 'none');
    }
}


const TooltipDiv = styled('div', {
    name: 'TooltipDiv'
})((props) => ({
    position: 'absolute',
    display: 'inline-block',
    bottom: '20px',
    width: '32px',
    minWidth: '28px',
}));

const TooltipTextSpan = styled('span', {
    name: "StyledTooltipTextSpan"
})({
    position: 'relative',
    visibility: 'visible',
    backgroundColor: 'white',
    color: 'text.primary',
    textAlign: 'center',
    padding: '1px 5px 1px 5px',
    borderRadius: '4px',
    zIndex: 1,
    fontFamily: 'Montserrat',
    fontSize: 11,
    width: '28px',
    minWidth: '28px',
    display: 'block',
    '&:after': {
        content: '""',
        position: 'absolute',
        top: '100%',
        left: '50%',
        marginLeft: '-5px',
        borderWidth: '5px',
        borderStyle: 'solid',
        borderColor: 'white transparent transparent transparent'
    }
});

const EventTooltip = (props: any) => {

    if (props.active && props.payload && props.payload.length) {
        return (
            <TooltipDiv sx={{
                left: props.coordinate.x + 1,
                top: ((7 - props.payload[1].payload.index) * 32) - (14 + 2 * props.payload[1].payload.index)
            }}>
                <TooltipTextSpan>{props.payload[0].payload.value}</TooltipTextSpan>
            </TooltipDiv>
        );
    }

    return null;
};

export default function EventsByTimeOfDayChart({ data }: EventsByTimeOfDayChartProps) {
    const theme = useTheme();

    return (
        <Fragment>
            <Stack
                sx={{
                    height: 320,
                    pb: 2,
                    width: 930
                }}
            >
                <ChartContainer>
                    <ResponsiveContainer>
                        <ScatterChart
                            margin={{
                                right: 32,
                                left: 0,
                            }}
                        >
                            <XAxis
                                type="category"
                                dataKey="hour"
                                allowDuplicatedCategory={false}
                                tick={AxisTick}
                                style={{
                                    fontFamily: 'Montserrat',
                                    fontWeight: 'bold',
                                    color: theme.palette.primary.main,
                                }}
                                padding={{ left: 0, right: 16 }}
                                tickLine={false}
                                axisLine={false}
                                interval={1}
                                dy={100}
                            />

                            <YAxis
                                allowDataOverflow={false}
                                type="category"
                                dataKey="day"
                                style={{
                                    fontSize: '1.2rem',
                                    fontFamily: 'Montserrat',
                                }}
                                minTickGap={0}
                                tickLine={false}
                                axisLine={false}
                                tick={YAxisTick}
                            />

                            <Tooltip content={<EventTooltip />} cursor={false} />

                            <Scatter data={data} shape={Square} onMouseOver={handleOver} onMouseOut={handleOut} />

                        </ScatterChart>
                    </ResponsiveContainer>

                </ChartContainer>
            </Stack>

            <Stack
                sx={{
                    height: 230,
                    justifyContent: "space-evenly",
                    minWidth: 110,
                    mt: '26px',
                }}>
                {Object.entries(chartLegends).map(([key, val]) => {
                    return (
                        <Stack direction="row" key={key}
                            sx={{
                                height: "25%",
                                alignItems: "center",
                                mb: '3px'
                            }}
                        >
                            <Box sx={{
                                width: 35,
                                height: "100%",
                                background: `linear-gradient(to top, ${val.min}, ${val.max})`
                            }}>
                                &nbsp;
                            </Box>
                            <Typography color="primary" sx={{
                                ml: 1
                            }}>
                                {key}
                            </Typography>
                        </Stack>
                    )
                })
                }


            </Stack>
        </Fragment>
    );
}
